/**
 * External dependencies
 */
import {
  round,
  capitalize,
  map,
  find,
  first,
  filter,
  keyBy,
  values,
  forOwn,
  merge,
  get,
  some,
  toString,
  findLastIndex,
  findIndex,
  isString,
} from "lodash";
import moment from "moment";
import i18n from "lib/i18n";

var formatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
});

export const moneyFormatter = formatter;

export const formatPercentage = (value) => {
  let normalizedNumber = parseFloat(value);
  return `${normalizedNumber.toFixed(2)}%`;
};

export const formatMoney = (value) => {
  if (isNaN(value)) {
    return formatter.format(0);
  }
  return formatter.format(value);
};

// some of the cube js methods dont expose access to short titles..
// it is going to be implemented at some point
//
// https://github.com/cube-js/cube.js/issues/673
//
export const toShortTitle = (name) => {
  return i18n.t(name);
};

// Retailer sends all data as capitals.. which.. does not look right on a chart
//
// This functions name is missleading, it is really a category name formatter
//
export const capitalizeChartPivotData = (chartPivotData) => {
  return map(chartPivotData, ({ category, x, ...rest }) => {
    let c = capitalize(x);

    // must pass second paramter to hide deprecation warning
    var date = moment(x);

    if (isNaN(x) && date.isValid()) {
      c = date.format("MMM DD");
    }

    return {
      category: c,
      x: c,
      ...rest,
    };
  });
};

// return a function which can format the given data based on paramters
// returned from cube JS
export const toTablePivotFormatter = ({ type, format } = {}) => {
  if (type === "number") {
    switch (format) {
      case "currency":
        return (value) => formatter.format(value);
      case "percent":
        return (value) => `${round(value, 2)}%`;
      default:
        return (value) => round(value, 2);
    }
  }

  switch (type) {
    case "time":
      return (value, _, options = { timezone: "Australia/Melbourne" }) => {
        var date = moment
          .utc(value)
          .tz(options?.timezone ?? "Australia/Melbourne");
        return date.format("MMM D [at] h:mm A");
      };
    default:
      return (value) => capitalize(value);
  }
};

export const toYAxisFormatter = (resultSet, config) => {
  const _r = resultSet.decompose();
  const _q = _r[0].query();

  const measure = _q["measures"][0];
  const measureAnnotation = _r[0].annotation().measures[measure];

  return toTablePivotFormatter({
    type: measureAnnotation?.type,
    format: measureAnnotation?.format,
  });
};

export const getPercentageChange = (oldNumber, newNumber) => {
  if (!oldNumber || !newNumber) {
    return 0;
  }

  if (oldNumber === 0 || newNumber === 0) {
    return 0;
  }

  var decreaseValue = oldNumber - newNumber;
  return (decreaseValue / oldNumber) * 100;
};

// this is only usefull for time series
// Since time series are always in the correct order, and the same amount of indexes
// it will be sufficient enough to just merge
export const mergeChartPivotResultsByIndex = (
  resultSetPivot,
  resultSetPivot2
) => {
  let r1 = [...resultSetPivot];
  let r2 = [...resultSetPivot2];

  r2 = map(r2, (s) => {
    let n = {};
    forOwn(s, (value, key) => {
      if (key === "x" || key === "category" || key === "xValues") {
        return;
      }
      return (n[key + "_compare"] = value);
    });
    return n;
  });

  // why were we filtering out categories?
  //return filter(merge(r1, r2), ({ category }) => !!category);

  return merge(r1, r2);
};

// If the x axis is a time series, then merge by position rather than category
export const mergeChartPivotResultsByCategory = (
  resultSetPivot,
  resultSetPivot2
) => {
  let r1 = [...resultSetPivot];
  let r2 = [...resultSetPivot2];

  // change all series keys to _compare
  r2 = map(r2, (s) => {
    let n = {};
    forOwn(s, (value, key) => {
      if (key === "x" || key === "category" || key === "xValues") {
        return (n[key] = value);
      }
      return (n[key + "_compare"] = value);
    });
    return n;
  });

  // merge the two data points by category
  let grouped = values(merge(keyBy(r1, "category"), keyBy(r2, "category")));

  // remove any data points that wernt in the original
  grouped = filter(grouped, ({ category }) => find(r1, { category }));

  return grouped;
};

export const translateFormatter = (value) => {
  return i18n.t(value);
};

export const normalizedXValue = (xValues = []) => {
  const firstValues = xValues[0];

  if (!firstValues) {
    return "";
  }

  // check if the first value is a date range
  const [startDateTime] = firstValues.split(" - ");

  const startMoment = moment
    .utc(startDateTime, "YYYY-MM-DDTHH:mm:ss.SSS", true)
    .local();

  // if the first value is a date range, then we need to format the date range
  if (!startMoment.isValid()) {
    return xValues.join(", ");
  }

  return xValues.slice(1).join(", ");
};

export const isDateString = (value) => {
  const startMoment = moment(value, "YYYY-MM-DDTHH:mm:ss.SSS", true);

  if (!startMoment.isValid()) {
    return false;
  }
  return true;
};

export const isDateRangeString = (value = "") => {
  if (isString(value) === false) {
    return false;
  }

  let v = value || "Unknown";
  const [startDateTime, endDateTime] = v.split(" - ");

  if (startDateTime && endDateTime) {
    if (isDateString(startDateTime) && isDateString(endDateTime)) {
      return true;
    }
  }
  return false;
};

export const isDateLikeString = (value) => {
  return isDateString(value) || isDateRangeString(value);
};

export const resultSetToTimeZone = (resultSet) => {
  const r = resultSet.decompose();
  const firstResult = r[0];
  const query = firstResult.query();
  return query.timezone ?? "Australia/Melbourne";
};

export const formatMaybeDateString = (value, dateFormat = "MMM D") => {
  const [maybeDateString, ...rest] = value.split(",");

  const isDateRange = isDateRangeString(maybeDateString);
  const isDate = isDateString(maybeDateString);

  if (false == ((isDateRange && !isDate) || (!isDateRange && isDate))) {
    return value;
  }

  let datesToFormat = [];

  if (isDateRange) {
    const [startDateTime, endDateTime] = maybeDateString.split(" - ");
    datesToFormat = [startDateTime, endDateTime];
  } else {
    datesToFormat = [maybeDateString];
  }

  let formattedDates = [];

  // when there is 2 days, and they are in the same month
  // we can format them as "1 - 2 Jan"
  if (datesToFormat.length === 2) {
    const d1 = moment.utc(datesToFormat[0]).local();
    const d2 = moment.utc(datesToFormat[1]).local();

    if (d1.year() === d2.year() && d1.month() === d2.month()) {
      formattedDates = [d1.format("D"), d2.format("D MMM")];
    }
  }

  if (formattedDates.length === 0) {
    formattedDates = datesToFormat.map((date) => {
      const d = moment.utc(date).local();
      if (!d.isValid()) {
        return "-";
      }
      return d.format(dateFormat);
    });
  }

  const formattedDate =
    formattedDates.length >= 2
      ? formattedDates.join(" - ")
      : first(formattedDates);

  if (rest.length === 0) {
    return formattedDate;
  }

  const formattedString = `${formattedDate}, ${rest.join(", ")}`;

  return formattedString;
};

export const formatResultSetToShortTitle = (queryType, series) => {
  if (queryType == "compareDateRangeQuery") {
    const yValues = get(series, "yValues", []);

    if (yValues.length < 2) {
      return "Unknown";
    }

    // Step 1: Split the first yValue into start and end date-time strings
    const [startDateTime, endDateTime] = yValues[0].split(" - ");

    // Step 2: Parse each date-time string using moment
    const startMoment = moment.utc(startDateTime).local();
    const endMoment = moment.utc(endDateTime).local();

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return "Unknown";
    }

    const m = yValues.slice(1).join(" ");
    const value = capitalize(m.replace("_", " ").split(".")[1]);

    if (startMoment.year() === endMoment.year()) {
      const formattedStartDate = startMoment.format("D MMM");
      const formattedEndDate = endMoment.format("D MMM YYYY");
      return `${formattedStartDate} - ${formattedEndDate}, ${value}`;
    } else {
      const formattedStartDate = startMoment.format("D MMM YYYY");
      const formattedEndDate = endMoment.format("D MMM YYYY");
      return `${formattedStartDate} - ${formattedEndDate}, ${value}`;
    }
  } else {
    return series.shortTitle;
  }
};

export function shouldColorCells(resultSet) {
  const r = resultSet.decompose();
  const firstResult = r[0];
  const query = firstResult.query();
  const hasTimeGranularity =
    query.timeDimensions.length >= 1 && query.timeDimensions[0].granularity;

  if (query.dimensions.length >= 1 && !hasTimeGranularity) {
    return true;
  }
  return false;
}

// depending on if the function has granuality and is a comparion query
// we will need to format the data differently
export function toNormalizedResultData(resultSet) {
  const originalResultset = resultSet.decompose()[0];

  const isGranularity = get(
    originalResultset.query(),
    "timeDimensions[0].granularity",
    false
  );

  if (isGranularity) {
    const result = resultSet.chartPivot();
    // there is a bug where the chartPivot returns the previous period when week is selected
    if (isGranularity == "week") {
      const original = resultSet.decompose()[0].chartPivot();

      return result.map((r, k) => {
        if (!original[k]) {
          return r;
        }

        const xValue = original[k]?.xValues[0];

        return {
          ...r,
          x: `${xValue} - ${moment(xValue)
            .add(1, "week")
            .format("YYYY-MM-DDT00:00:00.000")}`,
        };
      });
    }

    return result.map(({ xValues, x, ...r }) => {
      return {
        // the date is always the first value
        x: xValues[0],
        ...r,
      };
    });
  }
  const result = resultSet.chartPivot({
    x: ["x"],
    y: ["measures"],
  });

  if (result.length === 0) {
    return originalResultset.chartPivot({
      x: ["x"],
      y: ["measures"],
    });
  }

  return resultSet.chartPivot({
    x: ["x"],
    y: ["measures"],
  });
}

export function toNormalizedSeriesNames(resultSet) {
  const originalResultset = resultSet.decompose()[0];

  const isGranularity = get(
    originalResultset.query(),
    "timeDimensions[0].granularity",
    false
  );

  if (isGranularity) {
    return resultSet.seriesNames();
  }

  return resultSet.seriesNames({
    x: ["x"],
    y: ["measures"],
  });
}

export function isInFirstHalf(arr, index) {
  if (index < 0 || index >= arr.length) {
    return true;
  }
  const halfLength = Math.ceil(arr.length / 2);
  return index < halfLength;
}

export function toMomentObj(
  value,
  timeZone = "Australia/Melbourne",
  format = "YYYY-MM-DDTHH:mm:ss.SSS"
) {
  return moment.utc(value, format).tz(timeZone ?? "Australia/Melbourne");
}

export function getDayOfWeek(number) {
  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  // Adjust the index since arrays are 0-based but the numbers are 1-based
  return daysOfWeek[number - 1];
}

export function dayOfWeekFormatter(data) {
  // first add any of the missing hours
  let days = [];
  for (var i = 1; i <= 7; i++) {
    days.push({ x: toString(i), category: getDayOfWeek(i) });
  }

  let grouped = values(merge(keyBy(days, "x"), keyBy(data, "x")));

  return grouped.map((d) => ({ ...d, x: getDayOfWeek(d.x) }));
}

export function convertTo12HourFormat(hour) {
  if (hour < 0 || hour > 23) {
    return hour;
  }

  const period = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${hour12}${period}`;
}

export const hourOfDayDataFormatter = (data, query = {}) => {
  // first add any of the missing hours
  const measures = query.measures || [];

  let hours = [];
  for (var i = 0; i <= 23; i++) {
    hours.push({ x: toString(i), category: convertTo12HourFormat(i) });
  }
  let grouped = values(merge(keyBy(hours, "x"), keyBy(data, "x")));

  // Show only the hours that have data for give or take a few hours
  const firstIndex = findIndex(grouped, (d) =>
    some(measures, (x) => {
      return (d[x] ?? 0) > 0;
    })
  );

  const lastIndex = findLastIndex(grouped, (d) => {
    return some(measures, (x) => {
      return (d[x] ?? 0) > 0;
    });
  });

  const start = firstIndex < 0 ? 7 : Math.max(0, firstIndex - 2);
  const end =
    lastIndex < 0 ? 19 : Math.min(Math.max(19, grouped.length), lastIndex + 3);

  return grouped
    .slice(start, end)
    .map((d) => ({ ...d, x: convertTo12HourFormat(d.x) }));
};
